import React, { useState, useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { queryAllDocsByFieldSnap } from "../../../utils/firebase/Firebase.utils";
import { motion } from "framer-motion";
import {
  FiBell,
  FiCalendar,
  FiClock,
  FiSearch,
  FiFilter,
  FiFileText,
  FiAlertCircle,
  FiChevronRight,
  FiX,
  FiArrowLeft,
  FiEye,
  FiUser,
  FiTag,
} from "react-icons/fi";

function AllAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeType, setActiveType] = useState("All");

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const item = {
    hidden: { y: 10, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  useEffect(() => {
    const fetchAnnouncements = async () => {
      await queryAllDocsByFieldSnap(
        "announcements",
        "status",
        "==",
        "published",
        (data) => {
          // Sort by createdAt date in descending order (newest first)
          const sortedData = [...data].sort((a, b) => {
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          setAnnouncements(sortedData);
          setFilteredAnnouncements(sortedData);
          setLoading(false);
        }
      );
    };

    fetchAnnouncements();
  }, []);

  useEffect(() => {
    // Filter announcements based on search term and active type
    let filtered = [...announcements];

    // Filter by type if not "All"
    if (activeType !== "All") {
      filtered = filtered.filter(
        (announcement) =>
          announcement.type?.toLowerCase() === activeType.toLowerCase()
      );
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (announcement) =>
          announcement.title?.toLowerCase().includes(term) ||
          announcement.description?.toLowerCase().includes(term)
      );
    }

    setFilteredAnnouncements(filtered);
  }, [searchTerm, activeType, announcements]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAnnouncement(null);
  };

  const getTypeIcon = (type) => {
    type = type?.toLowerCase() || "";
    switch (type) {
      case "advisory":
        return <FiAlertCircle className="mr-1" />;
      case "circulars":
        return <FiFileText className="mr-1" />;
      case "events":
        return <FiCalendar className="mr-1" />;
      case "newsletters":
        return <FiFileText className="mr-1" />;
      default:
        return <FiBell className="mr-1" />;
    }
  };

  const getTypeColor = (type) => {
    type = type?.toLowerCase() || "";
    switch (type) {
      case "advisory":
        return "text-blue-600 bg-blue-100";
      case "circulars":
        return "text-green-600 bg-green-100";
      case "events":
        return "text-purple-600 bg-purple-100";
      case "newsletters":
        return "text-amber-600 bg-amber-100";
      default:
        return "text-[#363062] bg-[#363062]/10";
    }
  };

  const typeFilters = [
    "All",
    "Announcements",
    "Advisory",
    "Circulars",
    "Events",
    "Newsletters",
  ];

  // Render announcement card
  const renderAnnouncementCard = (announcement) => {
    return (
      <motion.div
        variants={item}
        className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all cursor-pointer"
        onClick={() => handleViewAnnouncement(announcement)}
      >
        <div className="p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div
              className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center ${getTypeColor(
                announcement.type
              )}`}
            >
              {getTypeIcon(announcement.type)}
              <span>{announcement.type || "Announcement"}</span>
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <FiClock className="mr-1 text-gray-400" size={10} />
              {formatDate(announcement.createdAt)}
            </div>
          </div>
          <h3 className="font-medium text-xs text-[#363062] mb-1 line-clamp-1">
            {announcement.title}
          </h3>
          <p className="text-xs text-gray-600 mb-2 line-clamp-3">
            {announcement.description}
          </p>
          <div className="flex justify-between items-center pt-1.5 border-t border-gray-100">
            <div className="text-xs text-gray-500 flex items-center">
              <FiUser className="mr-1 text-gray-400" size={10} />
              {announcement.createdBy?.split("@")[0] || "Admin"}
            </div>
            <div className="text-xs text-[#8FCD00] font-medium flex items-center">
              <span>Read more</span>
              <FiChevronRight className="ml-1" size={10} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render announcement modal
  const renderAnnouncementModal = () => {
    if (!selectedAnnouncement) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          {/* Modal Header */}
          <div className="border-b border-gray-200 p-3 flex justify-between items-center">
            <button
              onClick={closeModal}
              className="flex items-center text-[#363062] text-xs font-medium"
            >
              <FiArrowLeft className="mr-1.5" size={12} />
              Back
            </button>
            <button
              onClick={closeModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-3">
            {selectedAnnouncement.poster && (
              <div className="w-full h-48 overflow-hidden rounded-lg mb-3">
                <img
                  src={selectedAnnouncement.poster}
                  alt={selectedAnnouncement.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center ${getTypeColor(
                    selectedAnnouncement.type
                  )}`}
                >
                  {getTypeIcon(selectedAnnouncement.type)}
                  <span>{selectedAnnouncement.type || "Announcement"}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center">
                  <FiClock className="mr-1 text-gray-400" size={10} />
                  {formatDate(selectedAnnouncement.createdAt)}
                </div>
              </div>
              <h2 className="text-sm font-medium text-[#363062] mb-1.5">
                {selectedAnnouncement.title}
              </h2>
              <p className="text-xs text-gray-600 mb-3">
                {selectedAnnouncement.description}
              </p>
            </div>

            <div className="border-t border-gray-100 pt-2.5 mb-3">
              <h3 className="text-xs font-medium text-[#363062] mb-1.5">
                Details
              </h3>
              <div className="prose prose-sm text-gray-600 max-w-none">
                <p className="text-xs whitespace-pre-line">
                  {selectedAnnouncement.Details}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2.5 border-t border-gray-100">
              <div className="flex items-center text-xs text-gray-500">
                <FiUser className="mr-1 text-gray-400" size={10} />
                <span>
                  Posted by:{" "}
                  {selectedAnnouncement.createdBy?.split("@")[0] || "Admin"}
                </span>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <FiEye className="mr-1 text-gray-400" size={10} />
                <span>Published</span>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="border-t border-gray-200 p-3 flex justify-end">
            <button
              onClick={closeModal}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <UserScreenWindow label="Announcements">
      <div className="flex flex-col w-full bg-gray-50 min-h-screen pb-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col mb-4 bg-white shadow-sm border-b border-gray-100 p-3"
        >
          <div className="flex items-center">
            <div className="bg-[#363062] bg-opacity-10 p-1.5 rounded-lg mr-2">
              <FiBell className="text-[#363062] h-4 w-4" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Announcements
              </h1>
              <p className="text-xs text-gray-500">
                Stay updated with the latest news
              </p>
            </div>
          </div>
        </motion.div>

        {/* Search Bar */}
        <div className="px-3 mb-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <input
              type="search"
              placeholder="Search announcements..."
              className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062] bg-white"
              onChange={handleSearch}
              value={searchTerm}
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setSearchTerm("")}
              >
                <FiX className="w-3 h-3 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Filters Scrollable */}
        <div className="px-3 mb-4">
          <div className="mb-1.5 flex items-center">
            <FiFilter className="h-3 w-3 text-[#363062] mr-1.5" />
            <span className="text-xs font-medium text-[#363062]">
              Filter by type:
            </span>
          </div>
          <div className="flex overflow-x-auto pb-1 hide-scrollbar gap-1.5">
            {typeFilters.map((type, index) => {
              let icon;
              switch (type) {
                case "All":
                  icon = <FiTag size={10} className="h-2.5 w-2.5" />;
                  break;
                case "Announcements":
                  icon = <FiBell size={10} className="h-2.5 w-2.5" />;
                  break;
                case "Circulars":
                  icon = <FiFileText size={10} className="h-2.5 w-2.5" />;
                  break;
                case "Advisory":
                  icon = <FiAlertCircle size={10} className="h-2.5 w-2.5" />;
                  break;
                case "Events":
                  icon = <FiCalendar size={10} className="h-2.5 w-2.5" />;
                  break;
                case "Newsletters":
                  icon = <FiFileText size={10} className="h-2.5 w-2.5" />;
                  break;
                default:
                  icon = null;
              }

              return (
                <button
                  key={index}
                  onClick={() => setActiveType(type)}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-xs flex items-center whitespace-nowrap transition-colors ${
                    activeType === type
                      ? "bg-[#8FCD00] text-white shadow-sm"
                      : "bg-white text-gray-600 border border-gray-200"
                  }`}
                >
                  <span className="mr-1">{icon}</span>
                  <span style={{ fontSize: "10px" }}>{type}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Announcements List */}
        <div className="px-3">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-3 border-[#363062] border-t-transparent animate-spin mb-2"></div>
                <p className="text-[#363062] text-xs font-medium">Loading...</p>
              </div>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-lg shadow-sm p-4 text-center border border-gray-100"
            >
              <div className="bg-[#363062] bg-opacity-10 h-12 w-12 flex items-center justify-center rounded-full mx-auto mb-3">
                <FiAlertCircle className="h-6 w-6 text-[#363062]" />
              </div>
              <h3 className="text-xs font-medium text-[#363062] mb-1">
                No announcements found
              </h3>
              <p className="text-xs text-gray-500 max-w-xs mx-auto">
                {searchTerm
                  ? "No announcements match your search criteria."
                  : activeType !== "All"
                  ? `No ${activeType} announcements available.`
                  : "There are no published announcements available."}
              </p>
            </motion.div>
          ) : (
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="flex flex-col gap-3"
            >
              {filteredAnnouncements.map((announcement, index) => (
                <div key={announcement.id || index}>
                  {renderAnnouncementCard(announcement)}
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Announcement Modal */}
      {showModal && renderAnnouncementModal()}
    </UserScreenWindow>
  );
}

export default AllAnnouncements;
